<template>
  <div
    :style="backgroundStyle"
    :class="['universal-link-app', { 'has-fixed-background': hasFixedBackground }]"
  >
    <div v-if="failedRestricted" class="dsr-fail-container">
      <DsrUnavailable :logo-url="documentLogoUrl" />
    </div>
    <div v-else class="universal-gate-container">
      <TopBar v-if="!isDsr && !!verifyLinkResponse" :logo-url="documentLogoUrl" />
      <LobbyCard v-if="failedToVerify" :header="$t('the-content-is-not-available')">
        <FriedParagraph>
          {{ $t('the-content-is-not-available-description') }}
        </FriedParagraph>
      </LobbyCard>
      <div v-else-if="loading || !verifyLinkResponse" class="loader-container">
        <FriedSpinner />
      </div>
      <IdentifyRecipientModal v-else-if="!isDsr" :verify-link-response="verifyLinkResponse" />
      <IdentifyParticipantUniversal
        v-else
        :logo-url="documentLogoUrl"
        :verify-link-response="verifyLinkResponse"
      />
    </div>
  </div>
</template>
<script lang="ts">
import type { ComputedRef } from 'vue';
import { computed, defineComponent, onMounted, watch } from 'vue';
import { BrandingHelper } from '@getaccept/lib-shared-new/src/signing-theme/helpers/branding.helper';
import LobbyCard from '@getaccept/lib-shared-new/src/lobby/components/LobbyCard.vue';
import DsrUnavailable from '@getaccept/lib-shared-new/src/lobby/components/DsrUnavailable.vue';
import TopBar from '@getaccept/lib-shared-new/src/top-bar/components/TopBar.vue';
import { storeToRefs } from 'pinia';
import { BackgroundType } from '@getaccept/lib-shared-new/src/entity/types/enums/background-type';
import IdentifyParticipantUniversal from '@getaccept/dsr-published-site-new/src/identify/components/IdentifyParticipantUniversal.vue';
import { useThemeStore } from '../../theme/theme.store';
import router from '../../router';
import IdentifyRecipientModal from './IdentifyRecipientModal.vue';
import { useUniversalLink } from './composables/universal-link.composable';

export default defineComponent({
  components: {
    TopBar,
    LobbyCard,
    IdentifyRecipientModal,
    IdentifyParticipantUniversal,
    DsrUnavailable,
  },
  setup() {
    const themeStore = useThemeStore();
    const { theme } = storeToRefs(themeStore);

    const universalId: ComputedRef<string> = computed(() =>
      router.currentRoute.value.params.universalId.toString()
    );

    const {
      loading,
      failedToVerify,
      verifyLinkResponse,
      verifyLink,
      failedRestricted,
      universalLinkStorage,
    } = useUniversalLink();

    const hasFixedBackground = computed(
      () =>
        !theme.value ||
        theme.value.backgroundType === BackgroundType.Fixed ||
        !theme.value.backgroundImage
    );

    const backgroundStyle = computed(() =>
      theme.value?.backgroundImage
        ? `background-image: url(${theme.value?.backgroundImage});`
        : `background-image: url('/assets/img/background/signing-page-bg.svg');`
    );

    onMounted(() => {
      if (universalLinkStorage.value?.redirectURL) {
        const url = new URL(universalLinkStorage.value.redirectURL);
        url.searchParams.set('universalLinkURL', window.location.href);
        window.location.href = url.toString();
        return;
      }
      verifyLink(universalId.value);
    });

    const documentLogoUrl = computed(() => BrandingHelper.getLogoUrl(theme.value));

    const documentId = computed(() => verifyLinkResponse.value?.documentId);

    const isDsr = computed(() => verifyLinkResponse.value?.documentType === 'dsr');

    watch(documentId, () => {
      if (documentId.value) {
        themeStore.loadTheme(documentId.value);
      }
    });

    return {
      loading,
      failedToVerify,
      verifyLinkResponse,
      documentLogoUrl,
      documentId,
      backgroundStyle,
      isDsr,
      failedRestricted,
      hasFixedBackground,
    };
  },
});
</script>
<style lang="scss" scoped>
.universal-link-app {
  background: repeat scroll center;
  background-size: contain;
  min-height: 100%;

  &.has-fixed-background {
    background-repeat: no-repeat;
    background-attachment: fixed;
    background-size: cover;
  }
}

html {
  height: 100%;
  font-family: var(--default-font-family);

  body {
    margin: 0;
    height: 100%;
  }

  .universal-gate-container {
    height: 100vh;
    overflow: hidden;

    .loader-container {
      transform: translate(-50%, -50%);
      position: absolute;
      top: 50%;
      left: 50%;
      display: flex;
      background: white;
      border-radius: 100%;
      padding: var(--spacing-50);
    }
  }

  .dsr-fail-container {
    height: 100vh;
    background-image: url('../assets/gradient.png');
    background-repeat: no-repeat;
    background-size: cover;
    background-color: var(--gray-bg);
    background-position: center 10%;
    overflow: hidden;
  }
}
</style>
